/**
 * Text elements: laid out with opentype.js, rendered once into a layer canvas, and written on
 * along the analysed glyph centrelines (or wiped in left to right).
 */

import { makeCanvas } from "./canvas";
import { toPath2D, type Font, type GlyphSet } from "./fonts";
import type { TextProps } from "./project.gen";
import type { PlanStroke } from "./strokes";

export interface TextLayout {
  /** layer size in pixels */
  w: number;
  h: number;
  /** layer pixels per board unit */
  res: number;
  layer: OffscreenCanvas;
  /** pen strokes in layer pixels, reading order, underline last (right to left) */
  strokes: PlanStroke[];
}

interface PlacedGlyph {
  ch: string;
  x: number; // origin, board units relative to the text box
  y: number; // baseline
  path: Path2D;
  bbox: { x1: number; y1: number; x2: number; y2: number };
  synth?: SynthGlyph;
}

/** Symbols the font may lack, drawn as hand strokes (em units, baseline at y = 0, y down). */
interface SynthGlyph {
  adv: number;
  width: number; // stroke width, em
  lines: [number, number][][];
}

const SYNTH: Record<string, SynthGlyph> = {
  "→": { adv: 1.0, width: 0.075, lines: [[[0.08, -0.32], [0.9, -0.32]], [[0.66, -0.52], [0.9, -0.32], [0.66, -0.12]]] },
  "←": { adv: 1.0, width: 0.075, lines: [[[0.92, -0.32], [0.1, -0.32]], [[0.34, -0.52], [0.1, -0.32], [0.34, -0.12]]] },
  "↑": { adv: 0.62, width: 0.075, lines: [[[0.31, -0.02], [0.31, -0.74]], [[0.12, -0.53], [0.31, -0.74], [0.5, -0.53]]] },
  "↓": { adv: 0.62, width: 0.075, lines: [[[0.31, -0.74], [0.31, -0.02]], [[0.12, -0.23], [0.31, -0.02], [0.5, -0.23]]] },
  "✓": { adv: 0.8, width: 0.08, lines: [[[0.1, -0.36], [0.32, -0.1], [0.72, -0.72]]] },
  "✔": { adv: 0.8, width: 0.1, lines: [[[0.1, -0.36], [0.32, -0.1], [0.72, -0.72]]] },
  "✗": { adv: 0.72, width: 0.08, lines: [[[0.12, -0.66], [0.6, -0.06]], [[0.6, -0.66], [0.12, -0.06]]] },
  "✘": { adv: 0.72, width: 0.1, lines: [[[0.12, -0.66], [0.6, -0.06]], [[0.6, -0.66], [0.12, -0.06]]] },
  "•": { adv: 0.5, width: 0.17, lines: [[[0.25, -0.3], [0.2501, -0.3]]] },
};

/**
 * Lay out `props` and render it. Board-unit geometry: the text box starts at (pad, pad) in the
 * layer; the first baseline sits one ascender below its top.
 */
export function layoutText(font: Font, glyphs: GlyphSet, props: TextProps, res = 2): TextLayout {
  const size = props.size;
  const scale = size / font.unitsPerEm;
  const asc = font.ascender * scale;
  const desc = font.descender * scale; // negative
  const lineH = size * props.lineHeight;
  const lines = props.content.split("\n");

  // measure lines
  const measured = lines.map((line) => {
    const gs = font.stringToGlyphs(line);
    const xs: number[] = [];
    let x = 0;
    const chars = Array.from(line);
    const synth = gs.map((g, i) => (g.index === 0 ? SYNTH[chars[i]] : undefined));
    for (let i = 0; i < gs.length; i++) {
      xs.push(x);
      x += synth[i] ? synth[i]!.adv * size : (gs[i].advanceWidth ?? 0) * scale;
      if (i < gs.length - 1 && !synth[i] && !synth[i + 1]) x += font.getKerningValue(gs[i], gs[i + 1]) * scale;
    }
    return { gs, xs, width: x, chars, synth };
  });
  const boxW = Math.max(1, ...measured.map((m) => m.width));
  const boxH = asc - desc + (lines.length - 1) * lineH;
  const ul = props.underline;
  const pad = Math.ceil(Math.max(12, ul ? ul.width + Math.abs(ul.tilt) : 0, size * 0.15));
  const ulExtra = ul ? Math.max(0, ul.offset + ul.width) : 0;

  const placed: PlacedGlyph[] = [];
  measured.forEach((m, li) => {
    const lx = props.align === "center" ? (boxW - m.width) / 2 : props.align === "right" ? boxW - m.width : 0;
    const base = asc + li * lineH;
    m.gs.forEach((g, i) => {
      const ox = pad + lx + m.xs[i];
      const oy = pad + base;
      const sy = m.synth[i];
      if (sy) {
        const pts = sy.lines.flat();
        const xs = pts.map((q) => ox + q[0] * size);
        const ys = pts.map((q) => oy + q[1] * size);
        const r = (sy.width * size) / 2;
        const bbox = { x1: Math.min(...xs) - r, y1: Math.min(...ys) - r, x2: Math.max(...xs) + r, y2: Math.max(...ys) + r };
        placed.push({ ch: m.chars[i] ?? "", x: ox, y: oy, path: new Path2D(), bbox, synth: sy });
        return;
      }
      const p = g.getPath(ox, oy, size);
      placed.push({ ch: m.chars[i] ?? "", x: ox, y: oy, path: toPath2D(p), bbox: p.getBoundingBox() });
    });
  });

  const W = boxW + 2 * pad;
  const H = boxH + 2 * pad + ulExtra;
  const { canvas, ctx } = makeCanvas(W * res, H * res);
  ctx.scale(res, res);
  ctx.fillStyle = props.color;
  ctx.strokeStyle = props.color;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const g of placed) {
    if (!g.synth) {
      ctx.fill(g.path);
      continue;
    }
    ctx.lineWidth = g.synth.width * size;
    for (const line of g.synth.lines) {
      ctx.beginPath();
      line.forEach(([x, y], k) => (k ? ctx.lineTo(g.x + x * size, g.y + y * size) : ctx.moveTo(g.x + x * size, g.y + y * size)));
      ctx.stroke();
    }
  }

  const strokes: PlanStroke[] = [];
  for (const g of placed) {
    if (g.synth) {
      for (const line of g.synth.lines)
        strokes.push({ p: line.flatMap(([x, y]) => [(g.x + x * size) * res, (g.y + y * size) * res]), r: (g.synth.width * size * 0.5 + 1.5) * res });
      continue;
    }
    const data = glyphs.glyphs[g.ch];
    if (data) {
      for (const s of data.strokes) {
        const p = new Array<number>(s.p.length);
        for (let i = 0; i < s.p.length; i += 2) {
          p[i] = (g.x + s.p[i] * size) * res;
          p[i + 1] = (g.y + s.p[i + 1] * size) * res;
        }
        const r = typeof s.r === "number" ? s.r * size * res : s.r.map((v) => v * size * res);
        strokes.push({ p, r });
      }
    } else if (g.bbox.x2 > g.bbox.x1) {
      strokes.push(scribble(g.bbox, res));
    }
  }

  if (ul) {
    // underline under the last line's ink, right end raised by -tilt
    const inkX1 = Math.min(...placed.map((g) => g.bbox.x1).filter(Number.isFinite), pad);
    const inkX2 = Math.max(...placed.map((g) => g.bbox.x2).filter(Number.isFinite), pad + boxW);
    const y0 = pad + asc + (lines.length - 1) * lineH - desc + ul.offset;
    const y1 = y0 + ul.tilt;
    ctx.strokeStyle = ul.color;
    ctx.lineWidth = ul.width;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(inkX1, y0);
    ctx.lineTo(inkX2, y1);
    ctx.stroke();
    // drawn right to left: the pen carries on from the end of the word instead of flying back
    strokes.push({ p: [inkX2 * res, y1 * res, inkX1 * res, y0 * res], r: (ul.width / 2 + 2) * res });
  }
  return { w: canvas.width, h: canvas.height, res, layer: canvas, strokes };
}

/** Fallback pen path for a glyph without centreline data: a zig-zag over its box. */
function scribble(b: { x1: number; y1: number; x2: number; y2: number }, res: number): PlanStroke {
  const h = b.y2 - b.y1;
  const rows = Math.max(2, Math.ceil(h / 10));
  const step = h / rows;
  const p: number[] = [];
  for (let i = 0; i <= rows; i++) {
    const y = b.y1 + i * step;
    const [xa, xb] = i % 2 === 0 ? [b.x1, b.x2] : [b.x2, b.x1];
    p.push(xa * res, y * res, xb * res, y * res);
  }
  return { p, r: step * 0.75 * res };
}
